import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"
// BUTTON-8: Generate Draft and Suggest Tags go busy (spinner, label unchanged,
// aria-busy, no second click) while their request or the draft poll runs.
import { setButtonLoading } from "../lib/button_loading"

const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 60

// AI Writer panel (Task 11) — Admin::Marketing::BlogPostsController#generate_draft
// / #draft_status / #suggest_tags. Declared on the blog post editor's main
// <form> (alongside blog-seo-preview — see _form.html.haml) so it can target
// the title/slug/excerpt/body/meta_description fields that live outside the
// AI Writer partial itself, not just the transcript/guidance inputs inside it.
//
// Two different "Generate" behaviors depending on whether the post is
// persisted yet (see _ai_writer.html.haml):
//   - NEW record: no id to AJAX-post generate_draft against, so
//     generateNewRecord submits the main create form through the hidden
//     `then_generate=1` submitter (blocked when the transcript is empty).
//   - Persisted record: `generate` AJAX-posts to generate_draft and starts
//     polling draft_status, so the rest of the form's unsaved edits are left
//     alone (no page navigation).
//
// Applying a completed draft is always an explicit click (never automatic) —
// see `apply`.
// The LLM's slug is unvalidated optional output (Ai::BlogPostDrafter does not
// require it), so normalize it to the shape BlogPost's slug validation accepts
// (/\A[a-z0-9]+(?:-[a-z0-9]+)*\z/) before it lands in the field: accents
// dropped, lowercase, every run of other characters (underscores included)
// becomes one hyphen, and edge hyphens are trimmed (#11299). A slug that
// normalizes to "" leaves the field blank, so the server derives it from the title.
export function normalizeSlug(value) {
  return String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

export default class extends Controller {
  static targets = [
    "transcript", "guidance", "generateButton", "generateSubmit", "applyButton", "statusNote",
    "suggestTagsButton", "clearAiDraftFlag",
    "titleInput", "slugInput", "excerptInput", "metaDescriptionInput", "bodyInput",
  ]

  static values = {
    generateUrl: String,
    statusUrl: String,
    suggestTagsUrl: String,
    // Server-rendered @blog_post.ai_draft_status at page load — one of
    // "idle" | "queued" | "complete" | "failed". Drives connect() so a
    // completed/failed draft is visible on a fresh page load, not just
    // right after a Generate click in the SAME browser session:
    //   - "queued": resume polling (a mid-generation page refresh, or the
    //     redirect right after the new-record then_generate flow).
    //   - "complete"/"failed": these are already terminal — no interval
    //     needed, just one draft_status fetch to pull the persisted
    //     payload/error (never embedded directly in the page; the payload
    //     can be a full HTML draft body, so it stays server-side until
    //     asked for).
    draftState: String,
  }

  connect() {
    this._pollCount = 0
    this._polling = false
    this._interval = null
    this._draftPayload = null

    if (this.draftStateValue === "queued") {
      this.startPolling()
    } else if (this.draftStateValue === "complete" || this.draftStateValue === "failed") {
      this.#poll()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  // ---- New-record path: real form submission ----

  // Generate Draft on the New Post page is a type="button" (so Enter in a
  // field submits through Create Draft, the form's default button). It submits
  // the create form through the hidden `then_generate` submitter, which
  // carries `formnovalidate` (blank title is legal here) and the
  // then_generate=1 pair Turbo sends for the submitter.
  generateNewRecord(event) {
    event.preventDefault()
    if (this.#transcriptValue() === "") {
      this.#setStatus("Paste a transcript first.", true)
      return
    }
    if (!this.hasGenerateSubmitTarget) return

    const submitter = this.generateSubmitTarget
    setButtonLoading(this.generateButtonTarget, true)
    submitter.form.requestSubmit(submitter)
  }

  // ---- Persisted-record path: AJAX + polling ----

  async generate(event) {
    event.preventDefault()
    const transcript = this.#transcriptValue()
    if (!transcript) {
      this.#setStatus("Paste a transcript first.", true)
      return
    }
    if (!this.hasGenerateUrlValue || !this.generateUrlValue) return

    setButtonLoading(this.generateButtonTarget, true)
    this.#setStatus("Queuing draft…", false)

    try {
      const response = await post(this.generateUrlValue, {
        body: { transcript, guidance: this.#guidanceValue() },
        responseKind: "json",
      })

      if (response.ok || response.statusCode === 409) {
        this.startPolling()
      } else {
        const data = await response.json
        this.#setStatus((data && data.error) || "Could not start generation.", true)
        setButtonLoading(this.generateButtonTarget, false)
      }
    } catch {
      this.#setStatus("Could not start generation.", true)
      setButtonLoading(this.generateButtonTarget, false)
    }
  }

  async suggestTags(event) {
    event.preventDefault()
    if (!this.hasSuggestTagsUrlValue || !this.suggestTagsUrlValue) return
    if (!this.hasSuggestTagsButtonTarget) return

    setButtonLoading(this.suggestTagsButtonTarget, true)
    try {
      // suggest_tags is routed `post :suggest_tags` (config/routes.rb) — GET
      // does not resolve against it.
      //
      // Posts the CURRENT form field values, not just an id: right after
      // Generate -> Apply fills the form client-side, the DB row still holds
      // the OLD title/body until the admin clicks Save — reading persisted
      // content server-side would suggest tags for stale copy. The body
      // hidden field always holds TipTap's live HTML (tiptap_editor_controller
      // keeps it in sync on every edit), so it's the same "current content"
      // Apply itself writes into.
      const response = await post(this.suggestTagsUrlValue, {
        body: {
          title: this.hasTitleInputTarget ? this.titleInputTarget.value : "",
          body: this.hasBodyInputTarget ? this.bodyInputTarget.value : "",
        },
        responseKind: "json",
      })
      if (response.ok) {
        const data = await response.json
        this.#addSuggestedTags(data && data.tags)
      }
    } catch {
      // Silent — suggesting tags is a convenience, not a required step.
    } finally {
      setButtonLoading(this.suggestTagsButtonTarget, false)
    }
  }

  // ---- Polling ----

  startPolling() {
    if (this._polling) return
    this._polling = true
    this._pollCount = 0
    setButtonLoading(this.generateButtonTarget, true)
    this.#setStatus("Generating draft…", false)
    this._interval = setInterval(() => this.#poll(), POLL_INTERVAL_MS)
  }

  stopPolling() {
    this._polling = false
    if (this._interval) {
      clearInterval(this._interval)
      this._interval = null
    }
  }

  async #poll() {
    this._pollCount += 1
    if (this._pollCount > MAX_POLLS) {
      this.stopPolling()
      this.#setStatus("Still working — check back in a bit.", true)
      setButtonLoading(this.generateButtonTarget, false)
      return
    }
    if (!this.hasStatusUrlValue || !this.statusUrlValue) {
      this.stopPolling()
      return
    }

    try {
      const response = await get(this.statusUrlValue, { responseKind: "json" })
      if (!response.ok) return
      const data = await response.json

      if (data.status === "complete") {
        this.stopPolling()
        this._draftPayload = data.payload
        setButtonLoading(this.generateButtonTarget, false)
        if (this.hasApplyButtonTarget) this.applyButtonTarget.hidden = false
        this.#setStatus("Draft ready — Apply draft", false)
      } else if (data.status === "failed") {
        this.stopPolling()
        setButtonLoading(this.generateButtonTarget, false)
        this.#setStatus(data.error || "Generation failed.", true)
      }
    } catch {
      // Transient network hiccup — keep polling until MAX_POLLS.
    }
  }

  // ---- Apply (explicit click only) ----

  apply(event) {
    event.preventDefault()
    const payload = this._draftPayload
    if (!payload) return

    this.#setField(this.titleInputTarget, payload.title)
    this.#setField(this.slugInputTarget, normalizeSlug(payload.slug))
    this.#setField(this.excerptInputTarget, payload.excerpt)
    this.#setField(this.metaDescriptionInputTarget, payload.meta_description)

    if (this.hasBodyInputTarget) {
      this.bodyInputTarget.value = payload.body_html || ""
      // "change" (not "input") — tiptap_editor_controller#syncFromInput listens
      // for change specifically so an externally-set hidden field re-syncs the
      // TipTap document (see tiptap_editor_controller.js). blog_seo_preview
      // listens for both, so the SEO panel updates too.
      this.bodyInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.#addSuggestedTags(payload.suggested_tags)

    // Flags the NEXT Save draft/Update post submission to reset ai_draft_status
    // back to idle server-side (Admin::Marketing::BlogPostsController#update)
    // — otherwise it stays "complete" forever, and a later reload would show
    // "Draft ready — Apply draft" again even after the admin has since made
    // unrelated manual edits, inviting an accidental re-apply that clobbers them.
    if (this.hasClearAiDraftFlagTarget) this.clearAiDraftFlagTarget.value = "1"

    if (this.hasApplyButtonTarget) this.applyButtonTarget.hidden = true
    this.#setStatus("Draft applied.", false)
  }

  // ---- Helpers ----

  #transcriptValue() {
    return this.hasTranscriptTarget ? this.transcriptTarget.value.trim() : ""
  }

  #guidanceValue() {
    return this.hasGuidanceTarget ? this.guidanceTarget.value.trim() : ""
  }

  #setField(target, value) {
    if (!target) return
    target.value = value || ""
    target.dispatchEvent(new Event("input", { bubbles: true }))
  }

  #setStatus(message, isError) {
    if (!this.hasStatusNoteTarget) return
    this.statusNoteTarget.textContent = message
    this.statusNoteTarget.classList.toggle("gl-ai-writer__status--error", !!isError)
  }

  #addSuggestedTags(tags) {
    const chips = this.#tagChipsController
    if (!chips) return
    ;(tags || []).forEach((tag) => chips.addSuggestion(tag))
  }

  // The Tags rail panel (`blog-tag-chips`) lives outside this controller's own
  // element scope in the DOM tree in the sense that it's a sibling subtree,
  // not a target — go through the Stimulus application registry the same way
  // merge_field_picker_controller.js reaches tiptap-editor.
  get #tagChipsController() {
    const el = document.querySelector('[data-controller~="blog-tag-chips"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "blog-tag-chips") : null
  }
}
